import { useEffect, useRef, useState } from "preact/hooks";

import { InfoTooltip } from "../components/InfoTooltip";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { HELP_COPY } from "../lib/helpCopy";
import { enrollPasskey, isWebAuthnSupported } from "../lib/webauthn";
import {
  BIOMETRIC_STEP_ACCEPT,
  BIOMETRIC_STEP_BODY,
  BIOMETRIC_STEP_DECLINE,
  BIOMETRIC_STEP_FOOTER,
  BIOMETRIC_STEP_HEADING,
  BIOMETRIC_SOFT_FAIL,
  BIOMETRIC_SUCCESS,
} from "../lib/authCopy";

export interface PasskeyEnrollViewProps {
  /** Called once this step is fully resolved — success (after the brief
   * confirmation), soft-fail (after the brief reassurance), or "Not now".
   * The caller decides what's next (`app.tsx` returns to `me.refresh()` /
   * Today post-onboarding; `SignInSettingsView` just refreshes its own
   * passkey list post-re-offer) — this view never routes itself, matching
   * every other auth screen's `onDone`-only contract in this sprint. */
  onDone: () => void;
}

// UX §1.3: "brief confirmation, then straight into Today" / "then straight
// into Today exactly as if they'd tapped Not now" — long enough to read a
// short sentence, short enough not to feel like a stall.
const CONFIRMATION_DISPLAY_MS = 1500;

/**
 * Sprint-007 D6 (F4-5, UX §1.3) — the optional, skippable Face ID/
 * fingerprint opt-in step. Shown immediately after "Set my password"
 * succeeds (both first-time onboarding AND reset — UX §0's flow diagram:
 * "either path, on success -> optional Face ID/fingerprint step (§1.3) ->
 * Today"), and reused VERBATIM (UX §4.2: "do not write a second version of
 * this screen") for the Settings "+ Add a quick sign-in" re-offer.
 *
 * Engineering judgment, flagged in the PR (non-blocking, not a UX
 * deviation): if this browser has no WebAuthn support at all, "Turn it on"
 * would be a guaranteed dead-end soft-fail every time, so this view skips
 * itself entirely (calls `onDone()` on mount, renders nothing) rather than
 * showing a tap that can never succeed. This is never shown again
 * automatically either way (UX §1.3's "no re-nag" rule), so skipping loses
 * nothing a supported browser would have offered.
 */
export function PasskeyEnrollView({ onDone }: PasskeyEnrollViewProps) {
  const [phase, setPhase] = useState<"offer" | "working" | "success" | "soft-fail">("offer");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const supported = isWebAuthnSupported();

  useFocusHeadingOnMount(headingRef);

  useEffect(() => {
    if (!supported) onDone();
    // Mount-only: this screen never re-decides support mid-session.
  }, []);

  // UX §5 "Native prompts": after the platform prompt closes (success,
  // cancel, or error) focus must never be stranded on a dismissed native
  // dialog — moving focus into this screen's own status region (role
  // "status", aria-live "polite") guarantees a screen-reader user hears
  // the outcome even though the offer buttons are no longer on screen.
  useEffect(() => {
    if (phase === "success" || phase === "soft-fail") statusRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (phase !== "success" && phase !== "soft-fail") return undefined;
    const timer = window.setTimeout(onDone, CONFIRMATION_DISPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  if (!supported) return null;

  async function handleTurnOn() {
    setPhase("working");
    try {
      await enrollPasskey();
      setPhase("success");
    } catch {
      // UX §1.4/§1.3: every enrollment failure (platform decline, cancel,
      // an unsupported authenticator, a network hiccup) gets the SAME
      // soft-fail copy — never a raw browser/WebAuthn error string.
      setPhase("soft-fail");
    }
  }

  return (
    <div class="logged-out-screen">
      {/* Sprint-008 A5 (design-desktop-phase-a.md §4) — see `LoginView.tsx`'s
       * identical wrapper for the full rationale/contract. */}
      <div class="auth-card">
        <span class="app-wordmark">Hayloft</span>
        <h1 ref={headingRef} tabIndex={-1} class="logged-out-heading">
          {BIOMETRIC_STEP_HEADING}
          {phase === "offer" ? (
            <InfoTooltip
              label={HELP_COPY.passkeyEnrollment.label}
              content={HELP_COPY.passkeyEnrollment.content}
            />
          ) : null}
        </h1>

        {phase === "success" ? (
          <p ref={statusRef} tabIndex={-1} class="auth-subheading" role="status" aria-live="polite">
            {BIOMETRIC_SUCCESS}
          </p>
        ) : phase === "soft-fail" ? (
          <p ref={statusRef} tabIndex={-1} class="auth-subheading" role="status" aria-live="polite">
            {BIOMETRIC_SOFT_FAIL}
          </p>
        ) : (
          <>
            <p class="auth-subheading">{BIOMETRIC_STEP_BODY}</p>
            <div class="auth-form">
              <button
                type="button"
                class="primary-btn"
                disabled={phase === "working"}
                onClick={() => void handleTurnOn()}
              >
                {BIOMETRIC_STEP_ACCEPT}
              </button>
              <button
                type="button"
                class="secondary-btn"
                style={{ marginTop: "10px" }}
                disabled={phase === "working"}
                onClick={onDone}
              >
                {BIOMETRIC_STEP_DECLINE}
              </button>
            </div>
            <p class="auth-footer-hint">{BIOMETRIC_STEP_FOOTER}</p>
          </>
        )}
      </div>
    </div>
  );
}
